/**
 * Module Learning Configuration Editor
 *
 * Module 레벨 시작/종료 활동 설정 UI (퀴즈만 지원)
 */
import { useState } from 'react'
import { Play, CheckCircle, FileQuestion, ChevronDown, ChevronUp } from 'lucide-react'
import type { ModuleLearningConfig, ModuleActivityConfig, ScopedQuizOptions, QuestionSource } from '@/types/configuration'

interface ModuleLearningConfigEditorProps {
  config: ModuleLearningConfig | undefined
  onChange: (config: ModuleLearningConfig) => void
  overridable?: boolean
  onOverridableChange?: (overridable: boolean) => void
}

const defaultQuizOptions: ScopedQuizOptions = {
  questionCount: 10,
  shuffleQuestions: true,
  passingScore: undefined,
  timeLimitMinutes: undefined,
  questionSource: 'incorrect',
}

const QUESTION_SOURCE_OPTIONS: { value: QuestionSource; label: string; description: string }[] = [
  { value: 'checkup', label: 'Checkup 표시 문제 중에서', description: 'Checkup으로 지정된 문제들만 출제' },
  { value: 'incorrect', label: '오답 문제 중에서', description: '학습자가 틀린 문제들 중에서 출제' },
  { value: 'all', label: '전체적으로', description: 'Module 내 모든 문제에서 출제' },
]

const defaultActivityConfig: ModuleActivityConfig = {
  enabled: false,
  quizOptions: defaultQuizOptions,
}

export function ModuleLearningConfigEditor({ config, onChange, overridable, onOverridableChange }: ModuleLearningConfigEditorProps) {
  const [expandedSection, setExpandedSection] = useState<'start' | 'end' | null>('start')

  const startActivity = config?.startActivity ?? defaultActivityConfig
  const endActivity = config?.endActivity ?? defaultActivityConfig

  const updateStartActivity = (updates: Partial<ModuleActivityConfig>) => {
    onChange({
      ...config,
      startActivity: { ...startActivity, ...updates },
      endActivity: config?.endActivity,
    })
  }

  const updateEndActivity = (updates: Partial<ModuleActivityConfig>) => {
    onChange({
      ...config,
      startActivity: config?.startActivity,
      endActivity: { ...endActivity, ...updates },
    })
  }

  const renderActivityEditor = (
    activity: ModuleActivityConfig,
    updateActivity: (updates: Partial<ModuleActivityConfig>) => void,
    type: 'start' | 'end'
  ) => {
    const isExpanded = expandedSection === type
    const Icon = type === 'start' ? Play : CheckCircle
    const title = type === 'start' ? '시작 활동' : '종료 활동'
    const description = type === 'start'
      ? 'Module 학습 시작 시 표시되는 Checkup 퀴즈입니다.'
      : 'Module 학습 완료 후 표시되는 Checkup 퀴즈입니다.'

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpandedSection(isExpanded ? null : type)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Icon className={`w-5 h-5 ${type === 'start' ? 'text-amber-600' : 'text-amber-600'}`} />
            <div className="text-left">
              <h4 className="font-medium text-gray-900">{title}</h4>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={activity.enabled}
                onChange={(e) => updateActivity({ enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">활성화</span>
            </label>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {/* Content */}
        {isExpanded && activity.enabled && (
          <div className="p-4 space-y-4 border-t border-gray-200">
            {/* Quiz Info */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <FileQuestion className="w-5 h-5 text-amber-600" />
              <div>
                <span className="font-medium text-amber-900">Checkup 퀴즈</span>
                <p className="text-xs text-amber-700">Module내 Lesson들의 퀴즈들 중에서 Random하게 출제됩니다</p>
              </div>
            </div>

            {/* Question Source */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">출제 범위</label>
              <div className="space-y-1.5">
                {QUESTION_SOURCE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name={`questionSource-${type}`}
                      value={option.value}
                      checked={(activity.quizOptions?.questionSource ?? 'incorrect') === option.value}
                      onChange={() => updateActivity({
                        quizOptions: {
                          ...activity.quizOptions,
                          questionCount: activity.quizOptions?.questionCount ?? 10,
                          shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                          questionSource: option.value,
                        }
                      })}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div>
                      <span className="text-sm text-gray-900">{option.label}</span>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Quiz Options */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">문제 수</label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={activity.quizOptions?.questionCount ?? 10}
                    onChange={(e) => updateActivity({
                      quizOptions: {
                        ...activity.quizOptions,
                        questionCount: parseInt(e.target.value) || 10,
                        shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">기본값: 10문제</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">문제 부족 시</label>
                  <select
                    value={activity.quizOptions?.questionShortageStrategy ?? 'available_only'}
                    onChange={(e) => updateActivity({
                      quizOptions: {
                        ...activity.quizOptions,
                        questionCount: activity.quizOptions?.questionCount ?? 10,
                        shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                        questionShortageStrategy: e.target.value as 'available_only' | 'include_curriculum_incorrect',
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="available_only">있는 만큼만</option>
                    <option value="include_curriculum_incorrect">전체 커리큘럼에서 오답 문제도 포함</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">통과 점수 (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="미설정"
                    value={activity.quizOptions?.passingScore ?? ''}
                    onChange={(e) => updateActivity({
                      quizOptions: {
                        ...activity.quizOptions,
                        questionCount: activity.quizOptions?.questionCount ?? 10,
                        shuffleQuestions: activity.quizOptions?.shuffleQuestions ?? true,
                        passingScore: e.target.value ? parseInt(e.target.value) : undefined,
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">비워두면 점수와 무관하게 완료</p>
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={activity.quizOptions?.shuffleQuestions ?? true}
                  onChange={(e) => updateActivity({
                    quizOptions: {
                      ...activity.quizOptions,
                      questionCount: activity.quizOptions?.questionCount ?? 10,
                      shuffleQuestions: e.target.checked,
                    }
                  })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">문제 순서 랜덤</span>
              </label>
            </div>
          </div>
        )}

        {/* Disabled state message */}
        {isExpanded && !activity.enabled && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500 text-center">
              활성화하면 {title} 설정이 표시됩니다.
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="font-medium text-amber-900 mb-1">Module 레벨 학습 활동</h4>
        <p className="text-sm text-amber-700">
          각 Module 학습 시작 전/후에 표시되는 Checkup 퀴즈를 설정합니다.
          모든 Module에 동일하게 적용됩니다.
        </p>
      </div>

      {onOverridableChange && (
        <label className="flex items-center gap-2 p-3 bg-purple-50 border border-purple-100 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={overridable !== false}
            onChange={(e) => onOverridableChange(e.target.checked)}
            className="w-3.5 h-3.5 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <span className={`text-sm ${overridable !== false ? 'text-purple-700' : 'text-gray-500'}`}>
            하위 레벨에서 변경 가능
          </span>
        </label>
      )}

      {renderActivityEditor(startActivity, updateStartActivity, 'start')}
      {renderActivityEditor(endActivity, updateEndActivity, 'end')}
    </div>
  )
}
